import { TALENT_PAGE_KEY } from './constants';
import type { TTranslations } from './types';

export const en: TTranslations = {
  [TALENT_PAGE_KEY]: {
    PAGE_TITLE: 'Talent',
    PAGE_SUBTITLE: "Analyze your organization's performance by process.",
    BREADCRUMB_TALENT: 'Talent',
    FRESHNESS_PREFIX: 'Updated',

    TAB_GOALS: 'Goals',
    TAB_COMPETENCIES: 'Competencies',

    PROCESS_LOADING: 'Loading process',

    PROCESS_ENROLLED: '{{count}} enrolled collaborator',
    PROCESS_ENROLLED_plural: '{{count}} enrolled collaborators',
    PROCESS_STATUS_IN_PROGRESS: 'In progress',
    PROCESS_STATUS_CLOSED: 'Closed',

    EMPTY_TITLE: 'No processes yet',
    EMPTY_DESCRIPTION:
      "Once a process is created for this module, it'll show up here.",

    UNAVAILABLE_TITLE: 'Dashboard unavailable',
    UNAVAILABLE_DESCRIPTION:
      "Your organization hasn't contracted this Talent module.",

    ERROR_TITLE: 'We could not load the Talent dashboard',
    ERROR_RETRY: 'Retry',

    CONTENT_ERROR_TITLE: 'We could not load the information',
    CONTENT_ERROR_DESCRIPTION:
      'An error occurred while loading the data. Please try again.',

    // ANLT-599 — Tracking/Results mode
    MODE_TRACKING: 'Tracking',
    MODE_RESULTS: 'Results',
    MODE_HINT: 'Tracking: how the process is going. Results: how it went.',
    HOWTO_TITLE: 'How to read this dashboard',
    HOWTO_PROGRESS_VS_COMPLIANCE:
      'Tracking measures process progress (how many goals were loaded, approved, and reported). Results measures compliance (how well they did).',
    HOWTO_NO_RESULT_IS_NOT_ZERO:
      'Whoever didn\'t participate does not average as zero: it shows as "No result".',
    MODE_TRACKING_PENDING_TITLE: 'Tracking is coming soon',
    MODE_TRACKING_PENDING_DESCRIPTION:
      'The Tracking blocks of the goals cycle are not yet available on this dashboard.',
    MODE_RESULTS_PENDING_TITLE: 'Results is coming soon',
    MODE_RESULTS_PENDING_DESCRIPTION:
      'The Results blocks of the goals cycle are not yet available on this dashboard.',

    // ANLT-599 (task-05) — M-S1: cycle progress by stage
    CYCLE_HEADING: 'Cycle progress',
    CYCLE_DESCRIPTION:
      'How many collaborators are in each stage of the goals process.',
    CYCLE_TRACK_MISSING_NOTE:
      "This process doesn't have a tracking cycle defined yet, so self-report and boss report aren't measurable yet.",

    CYCLE_ENROLLED_LABEL: 'Enrolled collaborators',
    CYCLE_ENROLLED_TOOLTIP:
      'Counts collaborators assigned and active today. It can be higher than the «Goals load summary» report (which only counts those who already loaded goals) and decreases over time in a closed process.',
    CYCLE_STAGE_BREAKDOWN: '{{completed}} completed · {{pending}} pending',
    CYCLE_A11Y_BAND: 'Cycle progress by stage',

    STAGE_LOADED: 'Goals loaded',
    STAGE_APPROVED: 'Goals approved',
    STAGE_SELF_REPORTED: 'Self-report',
    STAGE_BOSS_REPORTED: 'Boss report',

    CYCLE_EMPTY_TITLE: 'No progress to show yet',
    CYCLE_EMPTY_DESCRIPTION:
      "This process doesn't have enrollments or tracking data loaded yet.",
    CYCLE_ERROR_TITLE: 'We could not load the cycle progress',

    // ANLT-600 (task-02) — M-S2: progress evolution
    EVOLUTION_HEADING: 'Progress evolution',
    EVOLUTION_DESCRIPTION:
      'How many collaborators completed each stage, day by day of the cycle.',
    EVOLUTION_EMPTY_TITLE: 'No days to draw yet',
    EVOLUTION_EMPTY_DESCRIPTION:
      "This process doesn't have a date window with tracking data yet.",
    EVOLUTION_ERROR_TITLE: 'We could not load the progress evolution',

    // ANLT-600 (task-03) — metadata line: process window + as-of date
    EVOLUTION_WINDOW: 'Cycle {{start}} → {{end}}',
    EVOLUTION_DATA_AS_OF: 'Data as of {{today}}',
    EVOLUTION_TODAY_OUTSIDE:
      'The process closed on {{end}}. Data is as of {{today}}.',

    // ANLT-600 (task-04) — honesty footnote (AC-4). Fixed precedence, plan
    // §8: `undefined` and `0` NEVER print a line (R3). `EVOLUTION_FOOTNOTE_NO_WINDOW`
    // is placeholder copy pending product confirmation (plan R-2).
    EVOLUTION_FOOTNOTE_TITLE: 'What the curve does not show',
    EVOLUTION_FOOTNOTE_UNDRAWN_STAGES:
      "The data source doesn't store a date for {{stages}}, so those stages can't be drawn. They're not zero: they're unknown.",
    EVOLUTION_FOOTNOTE_TRUNCATED:
      'The curve was cut on {{lastDay}} by the series point limit; the process continues until {{end}}.',
    EVOLUTION_FOOTNOTE_WITHOUT_DATE:
      "{{self}} collaborators in self-report and {{boss}} in leader evaluation completed the stage without a recorded date, so they don't appear on the curve.",
    EVOLUTION_FOOTNOTE_AFTER_CURVE:
      '{{self}} in self-report and {{boss}} in leader evaluation completed the stage after the last day of the series.',
    EVOLUTION_FOOTNOTE_RECONCILE:
      "That's why the curve can end below the «Cycle progress» cards: adding up the above, both numbers match.",
    EVOLUTION_FOOTNOTE_NO_WINDOW:
      'There is no date window for this process yet.',
    EVOLUTION_STAGE_LOADED: 'Goals loaded',
    EVOLUTION_STAGE_APPROVED: 'Goals approved',

    // ANLT-600 (task-05) — chart scaffold: the chart's own skeleton label
    // (distinct from the section-level Skeleton, task-02) + axis titles. The
    // Y axis is collaborators, never a percentage — this query has no
    // denominator (plan risk R-1).
    EVOLUTION_LOADING_LABEL: 'Loading the progress evolution',
    EVOLUTION_AXIS_X: 'Cycle day',
    EVOLUTION_AXIS_Y: 'Collaborators',

    // ANLT-600 (task-06) — two series + "today" mark (AC-2 visual, AC-3).
    // `EVOLUTION_TODAY_MARK` is `CrehanaReferenceLine`'s `label.value`,
    // rendered only when `today` is one of the axis's day categories.
    EVOLUTION_SERIES_SELF: 'Self-assessment',
    EVOLUTION_SERIES_BOSS: 'Manager evaluation',
    EVOLUTION_TODAY_MARK: 'Today',

    // ANLT-600 (task-07) — legend + final wiring. `EVOLUTION_A11Y_CHART` is
    // the outer chart wrapper's `aria-label` (outside
    // `CrehanaResponsiveContainer`), not a visual title.
    EVOLUTION_A11Y_CHART: 'Cycle progress evolution chart',

    // ANLT-599 (task-06) — blocking row, closes the story's block
    BLOCKS_HEADING: 'Blocks holding back the cycle',
    BLOCKS_ALERT: 'There are active blocks in this process.',
    BLOCKS_UNAVAILABLE: "We could not calculate this process's blocks.",
    BLOCK_WITHOUT_GOALS: 'No goals loaded',
    BLOCK_WITHOUT_GOALS_HINT: "Collaborators who haven't loaded any goal yet.",
    BLOCK_NOT_APPROVED: 'Goals pending approval',
    BLOCK_NOT_APPROVED_HINT: "Loaded goals their leader hasn't approved yet.",
    BLOCK_WITHOUT_SELF_REPORT: 'No self-report',
    BLOCK_WITHOUT_SELF_REPORT_HINT:
      "Collaborators who haven't self-reported yet.",

    // ANLT-606 (task-07) — aria-label for the overlay button on each
    // blocking card. {{label}} is one of the three BLOCK_* keys above.
    BLOCKS_CARD_ACTION: 'View collaborators in: {{label}}',

    // ANLT-606 — M-S5: collaborator detail
    DETAIL_HEADING: 'Collaborator detail',
    DETAIL_DESCRIPTION:
      'Each enrolled person, their stage, and their goals-cycle progress.',
    DETAIL_TABLE_TITLE: 'Collaborator detail',
    DETAIL_SEARCH_PLACEHOLDER: 'Search by name',
    DETAIL_COL_COLLABORATOR: 'Collaborator',
    DETAIL_COL_HEADQUARTER: 'Headquarters',
    DETAIL_COL_AREA: 'Area',
    DETAIL_COL_POSITION: 'Position',
    DETAIL_COL_BOSS: 'Leader',
    DETAIL_COL_GOALS: 'Goals',
    DETAIL_COL_GOALS_TOOLTIP: 'Goals loaded by the person.',
    DETAIL_COL_PROGRESS: 'Cycle progress',
    DETAIL_COL_PROGRESS_TOOLTIP:
      'Percentage of goals that reached the final stage.',
    DETAIL_COL_STAGE: 'Stage reached',
    DETAIL_COL_STAGE_TOOLTIP:
      "A stage counts as completed only when ALL of the person's goals reached it.",
    DETAIL_NO_BOSS: 'No leader assigned',
    DETAIL_NO_DATA_SR: 'No data',
    DETAIL_STAGE_NONE: 'No goals loaded',
    DETAIL_STAGE_LOADED: 'Goals loaded',
    DETAIL_STAGE_APPROVED: 'Goals approved',
    DETAIL_STAGE_SELF_REPORTED: 'Self-report',
    DETAIL_STAGE_BOSS_REPORTED: 'Boss report',
    DETAIL_EMPTY_TITLE: 'This process has no enrollments',
    DETAIL_EMPTY_DESCRIPTION:
      "There aren't any collaborators in this process yet.",
    DETAIL_SEARCH_EMPTY_TITLE: 'No collaborators match your search',
    DETAIL_SEARCH_EMPTY_DESCRIPTION:
      'Try another name, or clear the search to see everyone enrolled.',
    DETAIL_ERROR_TITLE: 'We could not load the collaborator detail',

    // ANLT-602 — M-S3: progress by segment (task-02: table columns)
    SEGMENT_TABLE_TITLE: 'Progress by group',
    SEGMENT_COL_GROUP: 'Group',
    SEGMENT_COL_ENROLLED: 'Collaborators',
    SEGMENT_COL_ENROLLED_TOOLTIP: 'Collaborators enrolled in this group.',
    SEGMENT_COL_PROGRESS: 'Cycle progress',
    SEGMENT_COL_PROGRESS_TOOLTIP:
      'Completed stages ÷ (group enrollment × cycle stages).',
    SEGMENT_STAGE_TOOLTIP: "% over this group's collaborators.",

    // ANLT-602 — M-S3 (task-03: heading/description/truncation/note/unfiltered empty/error)
    SEGMENT_HEADING: 'Cycle progress by segment',
    SEGMENT_DESCRIPTION:
      'How far along each group is in the goals cycle stages.',
    SEGMENT_TRUNCATED:
      'Showing the {{shown}} groups with the least progress, out of {{total}} total.',
    SEGMENT_NOTE:
      "Each percentage is over that group's collaborators, not the organization's total. Groups with no one enrolled show a dash.",
    SEGMENT_EMPTY_TITLE: "This process doesn't have any enrollments yet",
    SEGMENT_EMPTY_DESCRIPTION:
      'There are no groups to show for this process yet.',
    SEGMENT_ERROR_TITLE: 'We could not load the progress by segment',

    // ANLT-602 — M-S3 (task-04: selector + empty with an attribute chosen — closes the story)
    SEGMENT_SELECT_LABEL: 'View by',
    SEGMENT_SELECT_PLACEHOLDER: 'Choose an attribute',
    SEGMENT_GROUP_STANDARD: 'Standard attributes',
    SEGMENT_GROUP_CUSTOM: 'Custom fields',
    SEGMENT_GROUP_CUSTOM_TAG: 'From your company',
    SEGMENT_ATTRIBUTE_EMPTY_TITLE: "This attribute doesn't return any group",
    SEGMENT_ATTRIBUTE_EMPTY_DESCRIPTION:
      'Try another attribute, or go back to the default cut.',

    // ANLT-606 (task-06) — situation filter (D-15). "BLOCK_*" is reused from
    // ANLT-599 as-is. DETAIL_FILTERED_EMPTY_* is the SECOND filtered-empty
    // cause (the first is search, DETAIL_SEARCH_EMPTY_*): filtering by "no
    // goals loaded" on a process with enrollments is not "no enrollments"
    // either.
    DETAIL_FILTER_LABEL: 'Situation',
    DETAIL_FILTER_ALL: 'Everyone enrolled',
    DETAIL_FILTERED_EMPTY_TITLE: 'No collaborators in this situation',
    DETAIL_FILTERED_EMPTY_DESCRIPTION:
      'Try another situation, or clear the filter to see everyone enrolled.',

    // ANLT-604 — M-S4: progress by leader (task-02: table columns)
    LEADERS_TABLE_TITLE: 'Progress by leader',
    LEADERS_SEARCH_PLACEHOLDER: 'Search by name',
    LEADERS_COL_LEADER: 'Leader',
    LEADERS_COL_TEAM: 'Team',
    LEADERS_COL_PROGRESS: 'Cycle progress',

    // ANLT-604 — M-S4: progress by leader (task-03: section, states)
    LEADERS_HEADING: 'Progress by leader',
    LEADERS_DESCRIPTION:
      "How far along each leader's team is in the cycle stages.",
    LEADERS_EMPTY_TITLE:
      'This process has no leaders with an enrolled team yet',
    LEADERS_EMPTY_DESCRIPTION:
      'Once collaborators are enrolled, their leaders will show up here.',
    LEADERS_SEARCH_EMPTY_TITLE: 'No leader matches your search',
    LEADERS_SEARCH_EMPTY_DESCRIPTION:
      'Try another name, or clear the search to see every leader.',
    LEADERS_ERROR_TITLE: 'We could not load progress by leader',

    // ANLT-604 — M-S4: progress by leader (task-04: stage columns). Each stage label
    // (STAGE_LOADED, etc.) is REUSED from ANLT-599 via STAGE_LABEL_KEY — not redefined here.
    LEADERS_NOTE:
      "Each percentage is over that leader's enrolled team. Reached and pending stages are two separate readings: they are never added together.",
    LEADERS_STAGE_TOOLTIP:
      'On top, how many of the team reached this stage. Below, how many are still pending. Two separate readings: never added together.',
    LEADERS_PENDING: '{{count}} pending',
    LEADERS_PENDING_plural: '{{count}} pending',

    // ANLT-610 — M-R2: compliance distribution (semaphore, task-02: heading +
    // five states + AC-3 "no scale"). This block counts COLLABORATORS, not
    // goals (P-7, v5 contract) — copy never says "goals".
    DISTRIBUTION_HEADING: 'Compliance distribution',
    DISTRIBUTION_DESCRIPTION:
      'How many collaborators fall into each compliance range of the scale.',
    DISTRIBUTION_LOADING_LABEL: 'Loading compliance distribution',
    DISTRIBUTION_ERROR_TITLE: 'We could not load the compliance distribution',
    DISTRIBUTION_EMPTY_TITLE: 'This process has no evaluated collaborators yet',
    DISTRIBUTION_EMPTY_DESCRIPTION:
      'Once collaborators have a result, they will show up here distributed by range.',
    DISTRIBUTION_NO_SCALE_TITLE:
      'This cycle has no compliance scale configured',
    DISTRIBUTION_NO_SCALE_DESCRIPTION:
      'Configure a compliance scale to see collaborators distributed by range.',

    // ANLT-610 — M-R2 (task-03: band resolvers + scale legend, AC-1 legend
    // half). Each Tag's text is `band.label` verbatim — these keys only
    // cover the row label and the range copy. No "%": the SDL says the
    // top band's `upper_bound` is the process's `max_scale`, which may
    // exceed 100 — not guaranteed to be a percentage.
    DISTRIBUTION_SCALE_LABEL: 'Compliance scale',
    DISTRIBUTION_RANGE_BOTH: '{{lower}} – {{upper}}',
    DISTRIBUTION_RANGE_UP_TO: 'Up to {{upper}}',
    DISTRIBUTION_RANGE_FROM: 'From {{lower}}',

    // ANLT-610 — M-R2 (task-04: stacked bar chart, AC-1 chart half). The
    // three series are semantic (never by position), and the y-axis counts
    // COLLABORATORS, never "goals" (P-7).
    DISTRIBUTION_SERIES_CRITICAL: 'Critical',
    DISTRIBUTION_SERIES_COMPLIANT: 'Compliant',
    DISTRIBUTION_SERIES_BELOW: 'Below target',
    DISTRIBUTION_AXIS_X: 'Compliance band',
    DISTRIBUTION_AXIS_Y: 'Collaborators',
    DISTRIBUTION_A11Y_CHART: 'Compliance distribution chart',

    // ANLT-610 — M-R2 (task-05: evaluated-count note + "no result" footnote +
    // "nobody evaluated" sub-state, AC-2, closes MR-B). "No result" arrives
    // separately from the backend and is NEVER painted as another band —
    // folding it in would turn "not evaluated" into "scored the worst". The
    // note does not claim bands + without_result = enrolled (`enrolled_count`
    // does not travel in this payload, it lives in M-R1).
    DISTRIBUTION_EVALUATED_NOTE:
      'Percentages are calculated over {{count}} evaluated collaborators.',
    DISTRIBUTION_WITHOUT_RESULT_TITLE: 'No result',
    DISTRIBUTION_WITHOUT_RESULT_BODY:
      '{{count}} collaborators do not have a result yet: they are not part of the scale. Not being evaluated is not the same as scoring zero.',
    DISTRIBUTION_NO_EVALUATED_TITLE: 'Nobody has a result yet',
    DISTRIBUTION_NO_EVALUATED_DESCRIPTION:
      'Once collaborators are evaluated in this process, you will see how they are distributed on the scale.',

    // ANLT-609 (task-04) — banda de 5 tarjetas (M-R1)
    RESULTS_A11Y_BAND: 'Process results indicators',

    RESULTS_OFFICIAL_LABEL: 'Official compliance',
    RESULTS_OFFICIAL_TOOLTIP:
      'Percentage of evaluated collaborators who reached the threshold range configured by your organization. Counts people: it is not the average of their scores, so it does not move with a single outlier.',
    RESULTS_OFFICIAL_DESCRIPTION:
      'Counts people: {{compliant}} of {{evaluated}} evaluated reached the threshold.',

    RESULTS_COMPLIANT_LABEL: 'Collaborators who complied',
    RESULTS_COMPLIANT_TOOLTIP:
      'People whose individual result reached the "{{band}}" range or higher, according to the ranges your organization configured.',
    RESULTS_COMPLIANT_DESCRIPTION:
      'Of {{evaluated}} evaluated · "{{band}}" range or higher.',
    RESULTS_COMPLIANT_DESCRIPTION_NO_BAND:
      'Of {{evaluated}} evaluated · reached the threshold.',

    RESULTS_CRITICAL_LABEL: 'In critical status',
    RESULTS_CRITICAL_TOOLTIP:
      'People whose individual result fell in the lowest range configured by your organization. This is the group that needs an action plan.',
    RESULTS_CRITICAL_DESCRIPTION:
      'Of {{evaluated}} evaluated · "{{band}}" range.',
    RESULTS_CRITICAL_DESCRIPTION_NO_BAND:
      'Of {{evaluated}} evaluated · in the lowest range.',

    RESULTS_AVERAGE_LABEL: 'Average compliance',
    RESULTS_AVERAGE_TOOLTIP:
      "Mean of the evaluated collaborators' individual results. Averages scores: it moves entirely with a single outlier, which is why it can differ from the official figure without either being wrong.",
    RESULTS_AVERAGE_DESCRIPTION:
      'Averages scores from {{evaluated}} evaluated.',
    RESULTS_AVERAGE_DESCRIPTION_SCALE:
      'Averages scores from {{evaluated}} evaluated · scale 0–{{max}}.',

    RESULTS_COVERAGE_LABEL: 'Process coverage',
    RESULTS_COVERAGE_TOOLTIP:
      'Percentage of enrolled collaborators who finished with a recorded result. Anyone not evaluated does not average as zero: they are counted separately.',
    RESULTS_COVERAGE_DESCRIPTION:
      '{{evaluated}} evaluated out of {{enrolled}} enrolled.',

    // ANLT-609 (task-05) — sección + estados (M-R1)
    RESULTS_HEADING: 'Process results',
    RESULTS_DESCRIPTION:
      'How the organization did in the closed cycle. Here the word is compliance: how much of each goal was achieved.',
    RESULTS_TRACK_RANGE: '{{start}} – {{end}}',
    RESULTS_EMPTY_TITLE: 'This process has no enrolled collaborators',
    RESULTS_EMPTY_DESCRIPTION:
      'Once collaborators are enrolled and evaluated, you will see the process result here.',
    RESULTS_ERROR_TITLE: 'We could not load the process results',

    // ANLT-609 (task-07, MR-B) — no-scale micro-state (AC-3)
    RESULTS_NO_SCALE_CARD: 'No scale configured.',
    RESULTS_NO_SCALE_MESSAGE:
      'This process has no ranges configured, so there is no threshold to reach: official compliance, the collaborators who complied, and those in critical status cannot be calculated. They are not zero.',

    // ANLT-609 (task-08, MR-B) — uncovered declaration (AC-4)
    RESULTS_UNCOVERED_MESSAGE:
      '{{count}} collaborators with goals do not appear on this board because they have no active record. The downloadable report does include them, grouped in a single unnamed row.',

    // ANLT-617 (task-02) — `GoalsSegmentResultsBars`, group row (M-R3)
    RESULTS_GROUP_COVERAGE: '{{evaluated}} of {{total}} evaluated',
    RESULTS_GROUP_WITHOUT_RESULT: '{{count}} without a result',
    RESULTS_GROUP_AVERAGE_CAPTION: 'average',

    // ANLT-617 (task-03) — `GoalsSegmentResultsSection`, header + 5 states + totals (M-R3)
    RESULTS_GROUP_HEADING: 'Compliance by group',
    RESULTS_GROUP_DESCRIPTION:
      'How compliance is spread across your organization’s areas, locations, or roles.',
    RESULTS_GROUP_TOTALS:
      '{{groups}} groups · {{enrolled}} enrolled · {{evaluated}} evaluated',
    RESULTS_GROUP_NOTE:
      'Averages PEOPLE: each evaluated collaborator’s individual result in the group, on the scale your organization configured. Not comparable to an average that counts goals.',
    RESULTS_GROUP_TRUNCATED:
      'We are showing {{shown}} of {{total}} groups. The rest are not added up in this list.',
    RESULTS_GROUP_ATTRIBUTE_EMPTY_TITLE: 'This attribute yields no groups',
    RESULTS_GROUP_ATTRIBUTE_EMPTY_DESCRIPTION:
      'The process may well have people enrolled: it is this cut that yields no groups. Try another attribute, or go back to the default cut.',
    RESULTS_GROUP_EMPTY_TITLE: 'This process has no enrolled collaborators',
    RESULTS_GROUP_EMPTY_DESCRIPTION:
      'Once collaborators are enrolled and evaluated, you will see the cut by group here.',
    RESULTS_GROUP_ERROR_TITLE: 'We could not load the compliance by group',

    // ANLT-617 (task-04) — `row-track`, the stacked track (M-R3)
    RESULTS_GROUP_A11Y_BAR: 'How {{group}} is spread across the ranges',

    // ANLT-617 (task-05) — «no scale» micro-state + distribution note (M-R3)
    RESULTS_GROUP_NO_SCALE:
      'Your organization did not configure ranges for this process, so there is no distribution to show. Each group’s average still is.',
    RESULTS_GROUP_DISTRIBUTION_NOTE:
      'The bar shows how each group is spread across the ranges; the number is its average. Two groups with the same average can have opposite distributions.',

    // ANLT-614 (task-01) — `GoalsDisciplineSection`, header + 5 states + «no cycles
    // elapsed yet» sub-state (M-R5). Prefix DISCIPLINE_, NEVER RESULTS_ (that prefix
    // already has its own parity check and must not be contaminated while the parallel
    // Resultados branches are mid-flight).
    DISCIPLINE_HEADING: 'Tracking discipline through the cycle',
    DISCIPLINE_DESCRIPTION:
      'How disciplined the follow-up process was, not the result it produced.',
    DISCIPLINE_WHAT_IT_MEASURES:
      'Measures whether the person self-reported every approved goal in every cycle already under way. It is not the downloadable report’s “Complies” column, which measures whether the boss evaluated.',
    DISCIPLINE_NO_CYCLES_TITLE: 'There is nothing to measure yet',
    DISCIPLINE_NO_CYCLES_DESCRIPTION:
      'No cycle of this process has come due yet. Once the first one does, you will see here who kept the reporting ritual.',
    DISCIPLINE_LOADING_LABEL: 'Loading tracking discipline',
    DISCIPLINE_ERROR_TITLE: 'We could not load tracking discipline',
    DISCIPLINE_EMPTY_TITLE: 'This process has no enrolled collaborators',
    DISCIPLINE_EMPTY_DESCRIPTION:
      'Once collaborators are enrolled, you will see here who kept the ritual of reporting their goals.',

    // ANLT-614 (task-02) — `GoalsDisciplineKpiBand` + observed-tracks line (AC-3, G-2/G-4).
    // The percentage can render '—' while the three counts carry their real figure, including
    // a measured 0 — distinct causes, declared separately (see the tooltips).
    DISCIPLINE_OBSERVED_TRACKS:
      'Measured over {{tracks}} cycles already elapsed',
    DISCIPLINE_UP_TO_DATE_LABEL: 'Up to date',
    DISCIPLINE_UP_TO_DATE_TOOLTIP:
      'Self-reported every approved goal in every cycle already under way.',
    DISCIPLINE_PENDING_LABEL: 'Pending',
    DISCIPLINE_PENDING_TOOLTIP:
      'Had approved goals to report and still owe some follow-up.',
    DISCIPLINE_WITHOUT_GOALS_LABEL: 'Without goals',
    DISCIPLINE_WITHOUT_GOALS_TOOLTIP:
      'Had no approved goal at all: they count neither as up to date nor as pending.',
    DISCIPLINE_WITHOUT_GOALS_DESCRIPTION: 'Had nothing to report',
    DISCIPLINE_PERCENTAGE_LABEL: '% discipline',
    DISCIPLINE_PERCENTAGE_TOOLTIP:
      'Over those who had approved goals to report. Excludes anyone with nothing to report.',
    DISCIPLINE_PERCENTAGE_UNMEASURABLE: 'Nobody had approved goals to report',
    DISCIPLINE_A11Y_BAND: 'Tracking discipline indicators',

    // ANLT-614 (task-03) — `GoalsDisciplineGroupsChart` + `SegmentAttributeSelect` (AC-4,
    // G-5/G-7..G-10). The cut's empty state has TWO causes: with an attribute selected, the
    // message NAMES the enrolled people — NEVER "this process has no one enrolled", which was
    // already said to a process with 412 people twice in this epic.
    DISCIPLINE_CUT_EMPTY_TITLE: 'This cut returned no groups',
    DISCIPLINE_CUT_EMPTY_DESCRIPTION:
      'There are {{enrolled}} people enrolled; try another attribute',
    DISCIPLINE_GROUPS_EMPTY_TITLE: 'There are no groups to show yet',
    DISCIPLINE_NO_MEASURABLE_GROUPS: 'No group had approved goals to report',
    DISCIPLINE_GROUPS_EXCLUDED:
      'No bar in the chart (nobody had approved goals to report): {{groups}}',
    DISCIPLINE_TRUNCATED: '{{shown}} of {{total}}',
    DISCIPLINE_CHART_SERIES_LABEL: '% discipline',
    // ANLT-615 (task-03) — `GoalsByCategorySection`, header + 5 states
    // (M-R4). Own `CATEGORY_` prefix: zero keys shared with
    // `RESULTS_*`/`RESULTS_GROUP_*` — this block averages GOALS
    // (`average_goal_result`), not people.
    CATEGORY_HEADING: 'Compliance by goal category',
    CATEGORY_UNIT_NOTE:
      'This block averages goals, not people: the result is not comparable to the individual indicators from other blocks in this cycle.',
    CATEGORY_UNIT_TOOLTIP:
      'Each category averages the result of the goals it contains, regardless of who created them. It is not the same number as each person’s individual result in other blocks.',
    CATEGORY_LOADING_LABEL: 'Loading compliance by category',
    CATEGORY_ERROR_TITLE: 'We could not load the compliance by category',
    CATEGORY_EMPTY_TITLE: 'This process has no registered goals',
    CATEGORY_EMPTY_DESCRIPTION:
      'Once goals are created for this process, you will see compliance by category here.',
    // ANLT-615 (task-04) — `GoalsByCategoryCut`, cut-internal keys (do not
    // depend on whether the instance is "strategies" or "approaches"; those
    // two labels are added by task-05).
    CATEGORY_UNEVALUATED_TITLE: 'Not yet evaluated',
    CATEGORY_CAP_NOTE: 'Showing {{shown}} of {{total}} categories',
    CATEGORY_CUT_EMPTY: 'This cut has no categories',
    CATEGORY_SERIES_AVERAGE: 'Average compliance',
    // ANLT-615 (task-05) — wires both cuts inside the section (each
    // instance's label) + the coverage note. Closes the story.
    CATEGORY_CUT_STRATEGIES: 'Strategic goal',
    CATEGORY_CUT_APPROACHES: 'Approach',
    CATEGORY_UNCOVERED_NOTE:
      'This calculation does not include {{count}} goals whose owner has no active record. The downloadable report does include them.',

    // ANLT-618 (task-01) — `GoalsRankingSection`, collaborator ranking
    // (M-R6). Own `RANKING_` prefix, own vocabulary guard
    // (i18n/ranking-vocabulary.test.ts). `RANKING_COL_RESULT_TOOLTIP` says
    // "score", never "%" — `result` can exceed 100 (max_scale, plan §3.1).
    // Breakdown keys (`RANKING_GOALS_*`) are added by task-03.
    RANKING_HEADING: 'Collaborator ranking',
    RANKING_DESCRIPTION:
      'The result and band of everyone enrolled in this process, from best to worst compliance.',
    RANKING_TABLE_TITLE: 'Collaborator ranking',
    RANKING_COL_COLLABORATOR: 'Collaborator',
    RANKING_COL_RESULT: 'Result',
    RANKING_COL_RESULT_TOOLTIP:
      'The person’s score on the scale configured for this process.',
    RANKING_COL_BAND: 'Band',
    RANKING_NO_DATA_SR: 'No data',
    RANKING_UNEVALUATED: 'Not evaluated',
    RANKING_NO_SCALE: 'No scale configured',
    RANKING_EMPTY_TITLE: 'This process has no enrolled collaborators',
    RANKING_EMPTY_DESCRIPTION:
      'Once collaborators are enrolled in this process, you will see their ranking here.',
    RANKING_ERROR_TITLE: 'We could not load the collaborator ranking',

    // ANLT-618 (task-02) — ranking search, sort and attribute columns.
    // `RANKING_NO_BOSS` is the VISIBLE fallback for the Boss column,
    // distinct from the sr-only `RANKING_NO_DATA_SR` used by the other four.
    RANKING_COL_HEADQUARTER: 'Headquarters',
    RANKING_COL_AREA: 'Area',
    RANKING_COL_POSITION: 'Position',
    RANKING_COL_BOSS: 'Boss',
    RANKING_NO_BOSS: 'No boss assigned',
    RANKING_SEARCH_PLACEHOLDER: 'Search collaborator',
    RANKING_SEARCH_EMPTY_TITLE: 'We could not find anyone with that name',
    RANKING_SEARCH_EMPTY_DESCRIPTION: 'Try another name or check the spelling.',

    // ANLT-618 (task-03) — `GoalsRankingBreakdown`, per-goal breakdown of the
    // expanded row. `RANKING_GOAL_WEIGHT` IS a percentage (the weighting);
    // `RANKING_GOAL_RESULT` is a 0-100 score, never a "%" (plan §8.5) — two
    // numbers, two units, on the same row.
    RANKING_GOALS_TITLE: 'Goal breakdown',
    RANKING_GOAL_NAME: 'Goal',
    RANKING_GOAL_WEIGHT: 'Weight',
    RANKING_GOAL_RESULT: 'Result',
    RANKING_GOALS_EMPTY: 'This person has no registered goals',
    RANKING_GOALS_ERROR: 'We could not load the goal breakdown',
  },
};
